import { useCallback, useEffect, useRef, useState } from "react";

/**
 * 3D view with a PERSISTENT Map3DElement.
 *
 * Key difference from the Strapi source: the Strapi hook destroys and
 * recreates the Map3DElement on every 2D<->3D toggle, which is the source
 * of the choppiness. Here the element is created once on demand, then
 * hidden and shown. Camera state and polygons persist automatically.
 */

export interface Camera3DState {
  center: { lat: number; lng: number; altitude?: number };
  range: number;
  tilt: number;
  heading: number;
}

export function use3DView(
  mapInstanceRef: React.MutableRefObject<any>,
  zonesRef: React.MutableRefObject<any[]>,
  /**
   * Optional: returns the per-zone display path to render in 3D. If not
   * provided or returns null for a given zone, we fall back to the 2D
   * polygon's current path (which is what the previous behavior did).
   */
  getZone3DPath?: (
    zone: any,
  ) => Array<{ lat: number; lng: number }> | null,
) {
  const map3DContainerRef = useRef<HTMLDivElement | null>(null);
  const map3DRef = useRef<any>(null);
  // Map by zoneId so we can update a specific polygon during nudge.
  const polygon3DsRef = useRef<Map<string, any>>(new Map());
  const [is3DAvailable, setIs3DAvailable] = useState(false);
  const [is3DMode, setIs3DMode] = useState(false);
  const checkedRef = useRef<boolean | null>(null);
  const getPathFnRef = useRef(getZone3DPath);
  getPathFnRef.current = getZone3DPath;

  const check3DAvailability = useCallback(async () => {
    if (checkedRef.current !== null) return checkedRef.current;
    try {
      const maps3d = await window.google.maps.importLibrary("maps3d");
      const ok = !!(maps3d && maps3d.Map3DElement);
      checkedRef.current = ok;
      setIs3DAvailable(ok);
      return ok;
    } catch {
      checkedRef.current = false;
      setIs3DAvailable(false);
      return false;
    }
  }, []);

  const cleanup3DPolygons = useCallback(() => {
    polygon3DsRef.current.forEach((p) => {
      if (p.parentElement) p.parentElement.removeChild(p);
    });
    polygon3DsRef.current.clear();
  }, []);

  const syncZonesTo3D = useCallback(() => {
    if (!map3DRef.current) return;
    cleanup3DPolygons();
    const zones = zonesRef.current || [];
    zones.forEach((zone: any) => {
      if (!zone.polygon) return;

      // Prefer caller-supplied path (which can apply the 3D-specific offset).
      // Fall back to the polygon's current path if no override provided.
      let path: Array<{ lat: number; lng: number }> | null =
        getPathFnRef.current?.(zone) ?? null;
      if (!path) {
        const pathArr = zone.polygon.getPath().getArray();
        if (pathArr.length < 3) return;
        path = pathArr.map((ll: any) => ({
          lat: ll.lat(),
          lng: ll.lng(),
        }));
      }
      if (!path || path.length < 3) return;

      const hex = (zone.color || "#667E4E").replace("#", "");
      const poly3D = new window.google.maps.maps3d.Polygon3DElement({
        altitudeMode: "CLAMP_TO_GROUND",
        fillColor: `#${hex}73`,
        strokeColor: `#${hex}FF`,
        strokeWidth: 3,
        drawsOccludedSegments: true,
      });
      poly3D.path = path;
      map3DRef.current.append(poly3D);
      polygon3DsRef.current.set(zone.id, poly3D);
    });
  }, [cleanup3DPolygons, zonesRef]);

  /**
   * Update a single zone's 3D polygon path without rebuilding all of them.
   * Used by the nudge live-preview so keyboard arrow presses feel snappy.
   */
  const updateZone3DPath = useCallback(
    (zoneId: string, path: Array<{ lat: number; lng: number }>) => {
      const poly = polygon3DsRef.current.get(zoneId);
      if (!poly || !path || path.length < 3) return;
      try {
        poly.path = path;
      } catch {
        // No-op: some Map3DElement versions may throw on path re-assignment
        // during first paint. Next full sync will pick it up.
      }
    },
    [],
  );

  const ensure3DCreated = useCallback(() => {
    const container = map3DContainerRef.current;
    const map2D = mapInstanceRef.current;
    if (
      map3DRef.current ||
      !container ||
      !map2D ||
      !window.google?.maps?.maps3d
    )
      return;
    const center = map2D.getCenter();
    // Session 28: originally passed preserveDrawingBuffer:true here to
    // make canvas.toDataURL() reliable for 3D snapshot capture. Reverted
    // in Session 28 hotfix \u2014 Google's Map3DElement constructor throws
    // on the unknown option (surfaces as a cross-origin "Script error"
    // because google-maps is loaded cross-origin, opaque in React dev
    // overlay). Snapshot capture instead relies on the
    // requestAnimationFrame warm-up in captureMap.ts, which forces a
    // fresh paint immediately before toDataURL. If reps report blank 3D
    // snapshots in practice, escalate to the captureStream() plan
    // documented in SNAPSHOT_CAPTURE.md.
    const el = new window.google.maps.maps3d.Map3DElement({
      center: { lat: center.lat(), lng: center.lng(), altitude: 0 },
      range: 250,
      tilt: 45,
      heading: 0,
      mode: "HYBRID",
      defaultUIHidden: false,
    });
    el.style.width = "100%";
    el.style.height = "100%";
    container.appendChild(el);
    map3DRef.current = el;
    syncZonesTo3D();

    // Session 27 — Google's Map3DElement navigation controls (compass,
    // zoom, pan/tilt/rotate/elevation) render collapsed by default with
    // a small expand toggle at the bottom-right of the cluster. There's
    // no documented `defaultUIExpanded` attribute on the Map3DElement
    // API. Best-effort: after the element mounts, look for the collapse/
    // expand toggle in its shadow DOM (or a nested web component) and
    // click it once. Retries a few times to ride out the async render.
    // Silently no-ops if Google changes the internal structure — rep
    // just sees the default collapsed state, same as today.
    const clickedRef = { current: false };
    const tryExpand = () => {
      if (clickedRef.current) return;
      try {
        // Walk the element and any shadow roots it exposes. Selectors
        // ordered from most specific to most generic — first match wins.
        const roots: Array<Document | ShadowRoot | Element> = [];
        const seed: any = el;
        if (seed?.shadowRoot) roots.push(seed.shadowRoot);
        roots.push(seed);
        // Include any first-generation nested custom-element shadow roots.
        const walk = (node: any) => {
          const kids = node?.querySelectorAll?.("*") ?? [];
          for (const k of kids) {
            if ((k as any).shadowRoot) roots.push((k as any).shadowRoot);
          }
        };
        walk(seed?.shadowRoot ?? seed);

        const selectors = [
          'button[aria-label*="Expand" i]',
          'button[aria-label*="Show controls" i]',
          '[role="button"][aria-expanded="false"]',
          'button[part*="expand" i]',
        ];
        for (const root of roots) {
          for (const sel of selectors) {
            const btn: any = (root as any).querySelector?.(sel);
            if (btn && typeof btn.click === "function") {
              btn.click();
              clickedRef.current = true;
              return;
            }
          }
        }
      } catch {
        // no-op
      }
    };
    [250, 600, 1200, 2500].forEach((delay) => setTimeout(tryExpand, delay));
  }, [mapInstanceRef, syncZonesTo3D]);

  const enter3DMode = useCallback(() => {
    ensure3DCreated();
    setIs3DMode(true);
  }, [ensure3DCreated]);

  const exit3DMode = useCallback(() => {
    setIs3DMode(false);
  }, []);

  const toggle3D = useCallback(() => {
    if (is3DMode) exit3DMode();
    else enter3DMode();
  }, [is3DMode, enter3DMode, exit3DMode]);

  const getCameraState = useCallback((): Camera3DState | null => {
    const el = map3DRef.current;
    if (!el) return null;
    try {
      const c = el.center;
      return {
        center: {
          lat: Number(c?.lat),
          lng: Number(c?.lng),
          altitude: Number(c?.altitude) || 0,
        },
        range: Number(el.range),
        tilt: Number(el.tilt),
        heading: Number(el.heading),
      };
    } catch {
      return null;
    }
  }, []);

  const setCameraState = useCallback(
    (state: Camera3DState | null) => {
      if (!state?.center) return;
      ensure3DCreated();
      const el = map3DRef.current;
      if (!el) return;
      try {
        el.center = state.center;
        el.range = state.range;
        el.tilt = state.tilt;
        el.heading = state.heading;
      } catch {
        /* no-op */
      }
    },
    [ensure3DCreated],
  );

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      cleanup3DPolygons();
      if (map3DRef.current?.parentElement) {
        map3DRef.current.parentElement.removeChild(map3DRef.current);
      }
      map3DRef.current = null;
    };
  }, [cleanup3DPolygons]);

  return {
    map3DContainerRef,
    is3DAvailable,
    is3DMode,
    check3DAvailability,
    toggle3D,
    enter3DMode,
    exit3DMode,
    syncZonesTo3D,
    updateZone3DPath,
    getCameraState,
    setCameraState,
  };
}
